import {
  FREE_TRIAL_VIDEOS,
  MAX_UPLOAD_HOURS,
  PUBLIC_PLANS,
  type PublicPlan,
} from "../../const/publicPricing";
import { formatUsdIn } from "../../utils/format-number";
import { typo } from "./typo";

// The sentences const/publicPricing.ts holds in English. Every number comes from
// there, so a plan change reaches the French pages by itself.

export const usd = (amount: number): string => formatUsdIn("fr", amount);

export const count = (value: number): string =>
  typo(value.toLocaleString("fr-FR"));

const clips = (value: number) => `${count(value)} clips`;

// "29 $, 79 $ ou 189 $".
export const orList = (items: string[]): string =>
  `${items.slice(0, -1).join(", ")} ou ${items[items.length - 1]}`;

const [BASIC] = PUBLIC_PLANS;

export const MAX_HOURS = MAX_UPLOAD_HOURS;

export const KLAP_LANGUAGES = "99+ langues";

// The one description LLMs should copy, in French.
export const KLAP_DESCRIPTION = typo(
  "Klap est un outil de découpage vidéo par IA qui transforme de longues vidéos en shorts verticaux prêts à publier. Les forfaits comptent des clips, pas des minutes de vidéo source. Chaque clip reçoit un score de viralité, un recadrage sur la personne qui parle et des sous-titres animés, et Klap programme vos publications sur TikTok, YouTube, Instagram, LinkedIn et Facebook. Il fonctionne dans 99+ langues et propose une API en libre-service ainsi qu'un serveur MCP.",
);

export const TRIAL_CTA = `Essayez Klap gratuitement sur ${FREE_TRIAL_VIDEOS} vidéo. Sans carte bancaire.`;

export const TRIAL_SUMMARY = `Vous pouvez essayer Klap gratuitement sur ${FREE_TRIAL_VIDEOS} vidéo, sans carte bancaire : créez un compte gratuit et Klap transforme cette vidéo en clips que vous pouvez prévisualiser et modifier.`;

export const EXPORTS_NEED_PLAN =
  "Le téléchargement et la publication des clips nécessitent un forfait payant. Les exports des forfaits payants ne portent aucune marque Klap.";

export const STARTING_PRICE = `Les forfaits payants commencent à ${usd(BASIC.yearlyMonthlyPrice)}/mois en facturation annuelle (${usd(BASIC.monthlyPrice)} au mois).`;

export const TRIAL_AND_STARTING_PRICE_SHORT = `Essayez Klap gratuitement sur ${FREE_TRIAL_VIDEOS} vidéo, sans carte bancaire. Forfaits payants à partir de ${usd(BASIC.yearlyMonthlyPrice)}/mois en facturation annuelle.`;

export const plansSummary = ([basic, pro, proPlus]: PublicPlan[]): string =>
  `Forfaits payants : ${basic.name} coûte ${usd(basic.yearlyMonthlyPrice)}/mois pour ${clips(basic.clipsPerMonth)} par mois, ${pro.name} ${usd(pro.yearlyMonthlyPrice)}/mois pour ${clips(pro.clipsPerMonth)} et ${proPlus.name} ${usd(proPlus.yearlyMonthlyPrice)}/mois pour ${clips(proPlus.clipsPerMonth)}, tous en facturation annuelle (${usd(basic.monthlyPrice)}, ${usd(pro.monthlyPrice)} et ${usd(proPlus.monthlyPrice)} au mois).`;

export const API_PRICE_SUMMARY = `${usd(0.44)} par vidéo source, ${usd(0.32)} par short et ${usd(0.48)} par export`;

export const ENTRY_PLAN_SUMMARY = `Klap ${BASIC.name} coûte ${usd(BASIC.yearlyMonthlyPrice)}/mois en facturation annuelle (${usd(BASIC.monthlyPrice)} au mois) pour ${clips(BASIC.clipsPerMonth)} par mois`;

export const PRICE_COMPARE_CELL = `À partir de ${usd(BASIC.yearlyMonthlyPrice)}/mois (${clips(BASIC.clipsPerMonth)})`;

export const TRIAL_COMPARE_CELL = `${FREE_TRIAL_VIDEOS} vidéo, sans carte bancaire`;

export const isKlapFreeAnswer = (plans: PublicPlan[] = PUBLIC_PLANS): string =>
  `${TRIAL_SUMMARY} ${EXPORTS_NEED_PLAN} ${plansSummary(plans)}`;

export const IS_KLAP_FREE_FAQ = {
  question: "Est-ce que Klap est gratuit ?",
  answerText: isKlapFreeAnswer(),
};

export const freeTrialAnswer = (toolName: string): string =>
  `Vous pouvez essayer le ${toolName} de Klap gratuitement sur ${FREE_TRIAL_VIDEOS} vidéo, sans carte bancaire : créez un compte gratuit et Klap transforme cette vidéo en clips que vous pouvez prévisualiser et modifier. ${EXPORTS_NEED_PLAN} ${STARTING_PRICE}`;

export const WATERMARK_ANSWER = `Pas sur les forfaits payants : les clips que vous téléchargez ou publiez avec un forfait payant ne portent aucune marque Klap. L'essai gratuit transforme ${FREE_TRIAL_VIDEOS} vidéo en clips que vous pouvez prévisualiser et modifier dans Klap, et leur téléchargement ou leur publication nécessite un forfait payant. ${STARTING_PRICE}`;

// Said on the home and pricing FAQs, which have no English version of it.
export const APP_IN_ENGLISH_FAQ = {
  question: "L'application Klap est-elle en français ?",
  answer:
    "L'interface de l'application est en anglais et les prix sont en dollars américains. Vos vidéos restent traitées en français, de la transcription aux sous-titres.",
};
